import {AppliedFilterSection_Value} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/filter_section.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  FilterFlyoutIconName,
  HorizontalList,
  HorizontalListWrapper,
  Icon,
  Text,
  mediaTabletAndUp,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC, useMemo} from 'react';
import sc from 'styled-components';
import {usePotentialFilters} from '../../ProductStream/utils';

export const SearchFilter: FC<{
  filters: AppliedFilterSection_Value[];
  disableHorizontalList?: boolean;
  className?: string;
}> = ({filters, disableHorizontalList = false, className}) => {
  const {t} = useTranslation();
  const {filters: activeFilters, removeFilter} = usePotentialFilters();

  const normalizedSearchFilters = useNormalizeSearchFilters(filters);

  const activeSearchFilters = normalizedSearchFilters.filter(searchFilter => {
    const activeSearchFilters = activeFilters.get(searchFilter.slug);
    if (!activeSearchFilters) return false;

    return activeSearchFilters.includes(String(searchFilter.id));
  });

  if (activeSearchFilters.length === 0) return null;

  const searchFilterButtons = activeSearchFilters.map(filter => (
    <StyledButton
      key={`searchFilterSection-filter-${filter.id}`}
      variant="secondary"
      data-testid={`searchFilterSection-filter-${filter.id}`}
      onClick={() => {
        removeFilter(filter.slug, String(filter.id));
      }}
      aria-label={undefined}
    >
      {filter.label}
      <Icon
        data-testid={`searchFilterSection-filter-${filter.id}-removeIcon`}
        asset={FilterFlyoutIconName.Delete}
        color={COLORS.Grey1}
        size="XS"
      />
    </StyledButton>
  ));

  return (
    <Wrapper data-testid="searchFilterSection" className={className}>
      <Text color="Grey1" data-testid="searchFilterSection-label">
        {t('We also applied the following filters:')}
      </Text>
      {disableHorizontalList ? (
        <>{searchFilterButtons}</>
      ) : (
        <StyledHorizontalList>{searchFilterButtons}</StyledHorizontalList>
      )}
    </Wrapper>
  );
};

const useNormalizeSearchFilters = (filters: AppliedFilterSection_Value[]) => {
  return useMemo(() => {
    return filters.reduce((normalizedSearchFilters, {slug, value}) => {
      if (value?.$case === 'searchFilter') {
        const {id, label} = value.searchFilter;

        return [
          ...normalizedSearchFilters,
          {
            id,
            slug,
            label,
          },
        ];
      }

      return normalizedSearchFilters;
    }, [] as Array<{id: number; label: string; slug: string}>);
  }, [filters]);
};

const Wrapper = linariaStyled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  width: 100%;

  ${Text} {
    @media ${mediaTabletAndUp} {
      flex-basis: auto;
    }
  }
`;

const StyledHorizontalList = linariaStyled(HorizontalList)`
  width: 100%;
  ${HorizontalListWrapper} {
    gap: 0.5rem;
  }
`;

const StyledButton = sc(Button)`
  height: 2.5rem;
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  gap: 0.25rem;
  padding: 0.5rem;
  flex-shrink: 0;
  text-transform: capitalize;
`;
